import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchProfile, logout } from "../redux/actions/authActions";
import { markAttendance, resetAttendanceState } from "../redux/actions/attendanceActions";
import { parseQrParams, buildQrQueryString } from "../utils/queryParams";
import AuthCard from "../components/AuthCard";
import Spinner from "../components/Spinner";

export default function AttendancePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { profile, isLoadingProfile, profileError } = useSelector((state) => state.auth);
  const { isSubmitting, success, error } = useSelector((state) => state.attendance);

  const [overNight, setOverNight] = useState(false);
  const qrParams = parseQrParams(location.search);

  useEffect(() => {
    dispatch(fetchProfile());
    dispatch(resetAttendanceState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      navigate(`/success${buildQrQueryString(qrParams)}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const handleAttend = (attendType) => {
    dispatch(
      markAttendance({
        attendType,
        lid: qrParams.lid,
        bid: qrParams.bid,
        overNight,
      })
    );
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate(`/login${buildQrQueryString(qrParams)}`, { replace: true });
  };

  return (
    <AuthCard
      title="Qr Scanning Attendance Web Portal"
      headerAction={
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-1.5 text-sm font-semibold text-brand-navy/70 hover:text-brand-navy transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 17l5-5-5-5" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12H9" />
          </svg>
          Logout
        </button>
      }
    >
      <div className="flex flex-col gap-8">
        <div className="text-center">
          {isLoadingProfile ? (
            <div className="flex justify-center">
              <Spinner className="w-6 h-6 text-brand-navy" />
            </div>
          ) : profileError ? (
            <p className="text-red-600 text-sm">{profileError}</p>
          ) : (
            <p className="text-brand-text text-lg leading-relaxed">
              Hello{" "}
              <span className="font-semibold text-brand-navy">
                {profile?.firstName} {profile?.lastName}
              </span>
              <br />
              You have logged in as{" "}
              <span className="font-semibold text-brand-navy">{profile?.userName}</span>
            </p>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2 text-center">
            {error}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={() => handleAttend("in")}
            disabled={isSubmitting}
            className="flex-1 flex items-center justify-center gap-2 bg-brand-gold hover:bg-brand-gold-dark disabled:opacity-70 transition-colors text-brand-navy font-bold rounded-3xl py-3.5 text-lg"
          >
            {isSubmitting && <Spinner className="w-5 h-5" />}
            Check In
          </button>
          <button
            type="button"
            onClick={() => handleAttend("out")}
            disabled={isSubmitting}
            className="flex-1 flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navy-dark disabled:opacity-70 transition-colors text-white font-bold rounded-3xl py-3.5 text-lg"
          >
            {isSubmitting && <Spinner className="w-5 h-5" />}
            Check Out
          </button>
        </div>

        <label className="flex items-center justify-center gap-2 text-brand-text cursor-pointer select-none">
          <input
            type="checkbox"
            checked={overNight}
            onChange={(e) => setOverNight(e.target.checked)}
            className="w-4 h-4 accent-brand-navy"
          />
          Over Time
        </label>
      </div>
    </AuthCard>
  );
}
